import { useEffect, useState, createRef, forwardRef } from "react";
import styled, { TextInput, px, paneBoxShadow } from "livemap-ui";
import classNames from "classnames";

import { DialingCodeMap } from "../hooks/dialing-codes";
import { useOnClickOutside } from "../hooks/click-outside";

export interface DialingCodesProps {
  selected?: string;
  name?: string;
  dialingCodes: DialingCodeMap | null;
}

export interface DialingCodesDispatch {
  onChange?(dialingCode: string): void;
}

type Props = DialingCodesDispatch & DialingCodesProps;

export const DialingCode = forwardRef<HTMLInputElement, Props>(
  ({ selected, onChange, dialingCodes, name }, ref) => {
    const suggestionsRef = createRef<HTMLDivElement>();

    const [inputValue, setInputValue] = useState(selected || "46");
    const [filteredCodes, setFilteredCodes] = useState<string[]>([]);
    const [suggestionsVisible, setSuggestionsVisible] = useState(false);

    useOnClickOutside(suggestionsRef, () => setSuggestionsVisible(false));

    useEffect(() => {
      if (!dialingCodes) {
        setSuggestionsVisible(false);

        return;
      }

      const codes = Object.keys(dialingCodes)
        .filter((code) => code.indexOf(inputValue) === 0)
        .sort((left, right) => left.length - right.length)
        .slice(0, 4);

      setFilteredCodes(codes);
    }, [inputValue, dialingCodes]);

    const isValidCode = (code: string) => dialingCodes && dialingCodes[code];

    const selectCode = (code: string) => {
      if (isValidCode(code)) {
        setSuggestionsVisible(false);
        setInputValue(code);
        onChange && onChange(code);
      }
    };

    const isValidInput = isValidCode(inputValue);

    const getFlagURL = (code: string) =>
      dialingCodes
        ? `./flags/${dialingCodes[code].toLowerCase()}.svg`
        : undefined;

    return (
      <Container className={classNames({ valid: isValidInput })}>
        <CodeInput
          ref={ref}
          required={true}
          name={name}
          autoComplete="off"
          label="Country code"
          value={inputValue}
          onTextChange={(text) => setInputValue(text.replace(/\D/i, ""))}
          onFocus={() => setSuggestionsVisible(true)}
          onBlur={() => selectCode(inputValue)}
        />
        {isValidInput ? (
          <ValidIndicator>
            <FlagImage src={getFlagURL(inputValue)} />
            <Plus>+</Plus>
          </ValidIndicator>
        ) : null}
        <Suggestions ref={suggestionsRef}>
          {suggestionsVisible &&
            dialingCodes &&
            filteredCodes.map((code) => (
              <Suggestion key={code} onClick={() => selectCode(code)}>
                <FlagImage src={getFlagURL(code)} />
                <Code>{code}</Code>
              </Suggestion>
            ))}
        </Suggestions>
      </Container>
    );
  }
);

const ValidIndicator = styled.div`
  position: absolute;
  top: ${px(44)};
  left: 8px;
  pointer-events: none;
  display: flex;
`;

const Plus = styled.div`
  margin-left: 8px;
  font-size: 18px;
  line-height: 1.3;
`;

const CodeInput = styled(TextInput)`
  width: ${px(90)};
`;

const Container = styled.div`
  margin-right: ${px(12)};

  &.valid ${CodeInput} {
    transition: none;
    padding-left: ${px(50)};
    width: ${px(100)};
  }
`;

const FlagImage = styled.img`
  border-radius: 50%;
  width: ${px(24)};
  height: ${px(24)};
  border: 2px solid ${(p) => p.theme.borderColor};
`;

const Code = styled.div`
  margin-left: ${px(12)};
  font-family: ${(p) => p.theme.headingFontFamily};
  text-align: right;
`;

const Suggestions = styled.div`
  bottom: ${px(24)};
  margin-left: ${px(-6)};
  transform: translateY(100%);
  position: absolute;
  z-index: 4;
  min-width: 100px;
  box-shadow: ${paneBoxShadow()};
  background-color: ${(p) => p.theme.backgroundColor};
  border-radius: ${(p) => p.theme.borderRadius}px;
`;

const Suggestion = styled.div`
  position: relative;
  padding: ${px(10, 16)};
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;

  &:hover {
    background-color: ${(p) => p.theme.altBackgroundColor};
  }
`;
